import type { AgentToolDef } from './tool-defs';

/**
 * Headers the assistant adds to every API call it makes with the user's own session token.
 * They are user-intent signals for assistant-driven flows, not authentication.
 *
 * - `X-Sammi-Agent: 1` tags the call as assistant-initiated. The API then suppresses any
 *   email/SMS the call would send unless it also carries `X-Confirmed-Send: 1`.
 * - `X-Confirmed-Send: 1` is added ONLY for the one request that runs after the user
 *   clicked the confirm card's button (an Approval was consumed).
 * - `X-Agent-Tool` / `X-Agent-Run-Id` end up on the audit row.
 */
export const agentRequestHeaders = (
  def: Pick<AgentToolDef, 'name'>,
  options: { confirmed?: boolean; runId?: string } = {}
): Record<string, string> => ({
  'X-Sammi-Agent': '1',
  'X-Agent-Tool': def.name,
  ...(options.runId ? { 'X-Agent-Run-Id': options.runId } : {}),
  ...(options.confirmed ? { 'X-Confirmed-Send': '1' } : {}),
});

/**
 * Keeps only the extra body keys a tool declares (`extraBodyKeys`) and only string values.
 * These keys come from the confirm card (typed by the user), never from the model.
 */
export const pickExtraBody = (
  def: Pick<AgentToolDef, 'extraBodyKeys'>,
  extra: Record<string, unknown> | undefined
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!extra) return out;
  for (const key of def.extraBodyKeys ?? []) {
    const value = extra[key];
    if (typeof value === 'string' && value.trim()) out[key] = value.trim();
  }
  return out;
};
